"use client";

import { Card, Form, Spinner, Alert } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useDataFetch } from "../../hooks/use-data-fetch";
import Swal from "sweetalert2";

const SecuritySettings = () => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [hasPhone, setHasPhone] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const { getData, postData } = useDataFetch();

  const { data: statusData, isLoading: statusLoading } = getData(
    ["twoFactorStatus"],
    "/two-factor/status"
  );

  const { mutateAsync: enableTwoFactor } = postData("/two-factor/enable");
  const { mutateAsync: disableTwoFactor } = postData("/two-factor/disable");

  useEffect(() => {
    if (statusData?.data) {
      setTwoFactorEnabled(statusData.data.two_factor_enabled || false);
      setHasPhone(statusData.data.has_phone || false);
      setIsLoading(false);
    }
  }, [statusData]);

  const handleToggleTwoFactor = async () => {
    if (!hasPhone) {
      Swal.fire({
        icon: "warning",
        title: "Número de teléfono requerido",
        text: "Debes tener un número de celular configurado para activar la autenticación de doble factor.",
        confirmButtonText: "Entendido",
      });
      return;
    }

    if (!twoFactorEnabled) {
      const result = await Swal.fire({
        title: "Activar Autenticación de Doble Factor",
        html: `
          <p>¿Estás seguro de que deseas activar la autenticación de doble factor?</p>
          <p class="text-muted small">Se enviará un código de verificación a tu número de WhatsApp cada vez que inicies sesión.</p>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, activar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#0d6efd",
      });

      if (!result.isConfirmed) return;
    } else {
      const result = await Swal.fire({
        title: "Desactivar Autenticación de Doble Factor",
        text: "¿Estás seguro de que deseas desactivar la autenticación de doble factor?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, desactivar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc3545",
      });

      if (!result.isConfirmed) return;
    }

    try {
      setIsToggling(true);

      const response = twoFactorEnabled 
        ? await disableTwoFactor({})
        : await enableTwoFactor({});

      if (response?.data?.two_factor_enabled !== undefined) {
        setTwoFactorEnabled(response.data.two_factor_enabled);

        Swal.fire({
          icon: "success",
          title: response.data.two_factor_enabled ? "¡Activado!" : "¡Desactivado!",
          text: response.data.two_factor_enabled
            ? "La autenticación de doble factor ha sido activada exitosamente."
            : "La autenticación de doble factor ha sido desactivada.",
          confirmButtonText: "Entendido",
        });
      }
    } catch (error: any) {
      console.error("Error toggling 2FA:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Ocurrió un error al cambiar la configuración",
        confirmButtonText: "Entendido",
      });
    } finally {
      setIsToggling(false);
    }
  };


  if (statusLoading || isLoading) {
    return (
      <Card className="bg-white border-0 rounded-3 mb-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
            <span className="ms-2">Cargando configuración...</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-0 rounded-3 mb-4">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">Seguridad</h3>
        </div>

        <div className="border-bottom pb-4 mb-4">
          <h5 className="mb-3">Autenticación de Doble Factor (2FA)</h5>
          <p className="text-secondary mb-3">
            Agrega una capa adicional de seguridad a tu cuenta. Cada vez que inicies sesión,
            recibirás un código de verificación en tu WhatsApp.
          </p>

          {!hasPhone && (
            <Alert variant="warning" className="mb-3">
              <i className="ri-alert-line me-2"></i>
              Debes configurar un número de celular en tu perfil para usar esta función.
            </Alert>
          )}

          <div className="d-flex align-items-center justify-content-between bg-transparent border border-light p-3 rounded">
            <div className="d-flex align-items-center">
              <span className="material-symbols-outlined fs-24 text-primary me-3">
                {twoFactorEnabled ? "verified_user" : "shield"}
              </span>
              <div>
                <h6 className="mb-0">
                  {twoFactorEnabled ? "Activado" : "Desactivado"}
                </h6>
                <small className="text-muted">
                  {twoFactorEnabled
                    ? "Tu cuenta está protegida con 2FA"
                    : "Activa 2FA para mayor seguridad"}
                </small>
              </div>
            </div>

            <Form.Check
              type="switch"
              id="two-factor-switch"
              checked={twoFactorEnabled}
              onChange={handleToggleTwoFactor}
              disabled={isToggling || !hasPhone}
              className="fs-20"
            />
          </div>

        </div>

        <div>
          <h5 className="mb-3">Información de Seguridad</h5>
          <ul className="list-unstyled">
            <li className="mb-2">
              <i className="ri-check-line text-success me-2"></i>
              Cambia tu contraseña regularmente
            </li>
            <li className="mb-2">
              <i className="ri-check-line text-success me-2"></i>
              No compartas tu código 2FA con nadie
            </li>
            <li className="mb-2">
              <i className="ri-check-line text-success me-2"></i>
              Los códigos expiran en 10 minutos
            </li>
          </ul>
        </div>
      </Card.Body>
    </Card>
  );
};

export default SecuritySettings;
